import { ADSENSE_CLIENT_ID, ADSENSE_ENABLED } from "../config/env";

const ADSENSE_SCRIPT_ID = "adsense-script";
const FUNDING_CHOICES_SCRIPT_ID = "funding-choices-script";
let adsensePromise = null;
let fundingChoicesLoaded = false;

const canLoadAds = () =>
  typeof window !== "undefined" &&
  typeof document !== "undefined" &&
  ADSENSE_ENABLED &&
  Boolean(ADSENSE_CLIENT_ID);

const appendScript = ({ id, src, crossOrigin }) =>
  new Promise((resolve, reject) => {
    const existing = document.getElementById(id);
    if (existing) {
      if (existing.dataset.loaded === "true") {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = id;
    script.async = true;
    script.src = src;
    if (crossOrigin) {
      script.crossOrigin = crossOrigin;
    }
    script.addEventListener(
      "load",
      () => {
        script.dataset.loaded = "true";
        resolve();
      },
      { once: true }
    );
    script.addEventListener("error", reject, { once: true });
    document.head.appendChild(script);
  });

const signalGoogleFundingChoices = () => {
  if (!canLoadAds() || window.frames.googlefcPresent) {
    return;
  }

  if (!document.body) {
    window.setTimeout(signalGoogleFundingChoices, 100);
    return;
  }

  const iframe = document.createElement("iframe");
  iframe.name = "googlefcPresent";
  iframe.style.cssText =
    "width:0;height:0;border:none;z-index:-1000;left:-1000px;top:-1000px;display:none;";
  document.body.appendChild(iframe);
};

export function ensureFundingChoicesScript() {
  if (!canLoadAds() || fundingChoicesLoaded) {
    return;
  }

  fundingChoicesLoaded = true;
  signalGoogleFundingChoices();
  appendScript({
    id: FUNDING_CHOICES_SCRIPT_ID,
    src: "https://fundingchoicesmessages.google.com/i/pub-7703278859169278?ers=1",
  }).catch(() => {
    fundingChoicesLoaded = false;
  });
}

export function ensureAdsenseScript() {
  if (!canLoadAds()) {
    return Promise.reject(new Error("AdSense is not enabled"));
  }

  ensureFundingChoicesScript();

  if (!adsensePromise) {
    adsensePromise = appendScript({
      id: ADSENSE_SCRIPT_ID,
      src: `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`,
      crossOrigin: "anonymous",
    }).catch((err) => {
      adsensePromise = null;
      throw err;
    });
  }

  return adsensePromise;
}

export function scheduleAdsenseWarmup() {
  if (!canLoadAds()) {
    return () => {};
  }

  let done = false;
  let idleId = null;
  const isMobile = isMobileViewport();
  const timeoutId = isMobile ? null : window.setTimeout(load, 3500);
  const events = ["scroll", "pointerdown", "keydown", "touchstart"];

  function load() {
    if (done) {
      return;
    }
    done = true;
    cleanup();
    ensureAdsenseScript().catch(() => {});
  }

  function cleanup() {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }
    events.forEach((eventName) => {
      window.removeEventListener(eventName, load, { passive: true });
    });
    if (idleId && typeof window.cancelIdleCallback === "function") {
      window.cancelIdleCallback(idleId);
    }
  }

  events.forEach((eventName) => {
    window.addEventListener(eventName, load, { passive: true, once: true });
  });

  if (!isMobile && typeof window.requestIdleCallback === "function") {
    idleId = window.requestIdleCallback(load, {
      timeout: 2500,
    });
  }

  return cleanup;
}

export function isMobileViewport() {
  if (typeof window === "undefined") {
    return false;
  }
  return window.matchMedia("(max-width: 768px)").matches;
}
